import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { computeReadiness } from "../lib/readiness";
import { exportSeedPack, buildCsdbSeedPackBlob } from "../lib/seedPack";
import { exportBidPack, buildBidPackBlob } from "../lib/bidPack";
import SharePanel from "./SharePanel";
import HelpIconButton from "./HelpIconButton";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, ShieldCheck, Package, Briefcase } from "lucide-react";

const STATUS = {
  pass: { label: "PASS",    color: "#15803D", icon: CheckCircle2, bg: "bg-green-50",  border: "border-green-300" },
  warn: { label: "WARNING", color: "#CA8A04", icon: AlertTriangle, bg: "bg-yellow-50", border: "border-yellow-300" },
  fail: { label: "FAIL",    color: "#FF2400", icon: XCircle,      bg: "bg-red-50",    border: "border-red-300" },
};

const OVERALL = {
  pass: { label: "READY",        text: "All checks passed — bid is ready to submit.",                      cls: "bg-[#15803D]" },
  warn: { label: "WARNINGS",     text: "Estimate is acceptable but has open items worth confirming.",     cls: "bg-[#CA8A04]" },
  fail: { label: "NOT READY",    text: "Critical checks failed — resolve them before submitting the bid.", cls: "bg-[#FF2400]" },
};

export default function ReadinessTab() {
  const { t } = useTranslation();
  const { estimate, totals, resourcing, brex, dml, risk, schedule, bidPrice } = useEstimate();
  const r = useMemo(() => computeReadiness(estimate, totals, resourcing, brex, risk), [estimate, totals, resourcing, brex, risk]);
  const [shareState, setShareState] = useState({
    seed: { uploading: false, link: null, filename: null, size: null, token: null },
    bid:  { uploading: false, link: null, filename: null, size: null, token: null },
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const handleSeedPack = async () => {
    try {
      const m = await exportSeedPack({ estimate, totals, resourcing, brex, dml, readiness: r });
      toast.success("CSDB seed pack downloaded", {
        description: `${m.files.length} files · ${m.counts.brexDecisionPoints} BR DPs`,
      });
    } catch (err) {
      toast.error("Failed to build seed pack", { description: String(err.message || err) });
    }
  };

  const handleBidPack = async () => {
    try {
      const m = await exportBidPack({ estimate, totals, resourcing, brex, dml, readiness: r, risk, schedule, bidPrice });
      toast.success("Bid pack downloaded", {
        description: `${m.files.length} files · ${m.risk.count} risks · ${m.schedule.monthCount} months · final ${m.bid.currencySymbol}${m.bid.finalPrice.toLocaleString()}`,
      });
    } catch (err) {
      toast.error("Failed to build bid pack", { description: String(err.message || err) });
    }
  };

  const buildPayload = (kind) => kind === "bid"
    ? { estimate, totals, resourcing, brex, dml, readiness: r, risk, schedule, bidPrice }
    : { estimate, totals, resourcing, brex, dml, readiness: r };

  const handleCreateShareLink = async (kind) => {
    if (!estimate.projectName) {
      toast.error("Set a project name first", { description: "It's used in the pack filename." });
      return;
    }
    setShareState((s) => ({ ...s, [kind]: { ...s[kind], uploading: true } }));
    try {
      const builder = kind === "bid" ? buildBidPackBlob : buildCsdbSeedPackBlob;
      const { blob, filename } = await builder(buildPayload(kind));
      const qs = new URLSearchParams({
        projectId: estimate.id || "",
        projectName: estimate.projectName || "",
        filename,
      }).toString();
      const resp = await fetch(`${backendUrl}/api/estimator/share/seedpack?${qs}`, {
        method: "POST",
        headers: { "Content-Type": "application/zip" },
        body: blob,
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const link = `${backendUrl}${data.path}`;
      setShareState((s) => ({
        ...s,
        [kind]: { uploading: false, link, filename: data.filename, size: data.size, token: data.token },
      }));
      toast.success("Share link ready", { description: "Link copied — paste in chat / email." });
      try { await navigator.clipboard.writeText(link); } catch { /* clipboard may be blocked */ }
    } catch (err) {
      setShareState((s) => ({ ...s, [kind]: { ...s[kind], uploading: false } }));
      toast.error("Failed to create share link", { description: String(err.message || err) });
    }
  };

  const handleCopy = async (kind) => {
    const link = shareState[kind]?.link;
    if (!link) return;
    try { await navigator.clipboard.writeText(link); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select the link manually"); }
  };

  const handleRevoke = async (kind) => {
    const token = shareState[kind]?.token;
    if (!token) return;
    try {
      await fetch(`${backendUrl}/api/estimator/share/seedpack/${token}`, { method: "DELETE" });
      setShareState((s) => ({ ...s, [kind]: { uploading: false, link: null, filename: null, size: null, token: null } }));
      toast.success("Share link revoked");
    } catch {
      toast.error("Failed to revoke link");
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const c of r.checks) {
      g[c.dimension] = g[c.dimension] || [];
      g[c.dimension].push(c);
    }
    return g;
  }, [r.checks]);

  const overall = OVERALL[r.overall];

  return (
    <section className="space-y-6" data-testid="readiness-tab">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
          {t("readiness.section")}
        </p>
        <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
          {t("readiness.title")}
          <HelpIconButton anchor="ch4" testId="help-icon-readiness" />
        </h2>
        <p className="text-sm text-gray-600 mt-1 max-w-2xl">
          {t("readiness.description")}
        </p>
      </div>

      {/* OVERALL GATE */}
      <div className={"text-white p-6 flex flex-wrap items-center justify-between gap-4 " + overall.cls} data-testid="readiness-gate">
        <div className="flex items-center gap-4">
          <ShieldCheck size={36} strokeWidth={2} />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">
              Overall Status
            </p>
            <p className="font-heading text-3xl font-black leading-none mt-1" data-testid="readiness-overall-label">
              {overall.label}
            </p>
            <p className="text-sm text-white/90 mt-2 max-w-2xl">{overall.text}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">
            Score
          </p>
          <p className="font-mono text-5xl font-black leading-none mt-1" data-testid="readiness-score">
            {r.score}
          </p>
          <p className="font-mono text-xs text-white/80 mt-2">
            <span data-testid="readiness-pass-count">{r.counts.pass}</span> pass ·
            {" "}<span data-testid="readiness-warn-count">{r.counts.warn}</span> warn ·
            {" "}<span data-testid="readiness-fail-count">{r.counts.fail}</span> fail
          </p>
        </div>
      </div>

      {/* CHECKS GROUPED BY DIMENSION */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(grouped).map(([dim, items]) => (
          <div key={dim} className="bg-white border border-gray-300" data-testid={`readiness-group-${dim.toLowerCase()}`}>
            <div className="border-b border-gray-300 p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
                Dimension
              </p>
              <h3 className="font-heading text-lg tracking-tight font-bold text-black">{dim}</h3>
            </div>
            <ul className="divide-y divide-gray-200">
              {items.map((c) => {
                const s = STATUS[c.status];
                const Icon = s.icon;
                return (
                  <li
                    key={c.id}
                    className={"p-4 flex items-start gap-3 " + s.bg}
                    data-testid={`readiness-check-${c.id}`}
                  >
                    <Icon size={18} strokeWidth={2} style={{ color: s.color }} className="mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-semibold text-black text-sm">{c.label}</p>
                        <span
                          data-testid={`readiness-status-${c.id}`}
                          className="font-mono text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border"
                          style={{ color: s.color, borderColor: s.color }}
                        >
                          {s.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-700 mt-1 font-mono">{c.message}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {/* DOWNLOADS — Seed Pack + Bid Pack */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-300 p-5" data-testid="seedpack-panel">
          <div className="flex items-center gap-2">
            <Package size={16} className="text-[#002FA7]" />
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">CSDB Seed Pack</p>
          </div>
          <h3 className="font-heading text-lg tracking-tight font-bold text-black">One-button bid bundle</h3>
          <p className="text-sm text-gray-600 mt-1">
            BREX skeleton + SNS hierarchy + readiness scorecard + manifest.
          </p>
          <button
            onClick={handleSeedPack}
            data-testid="seedpack-export-btn"
            className="mt-4 bg-[#002FA7] text-white hover:bg-black rounded-none px-4 py-2.5 font-bold text-sm inline-flex items-center gap-2"
          >
            <Package size={14} /> Download Seed Pack (.zip)
          </button>
        </div>

        <div className="bg-white border-2 border-[#002FA7] p-5" data-testid="bidpack-panel">
          <div className="flex items-center gap-2">
            <Briefcase size={16} className="text-[#002FA7]" />
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#002FA7]">Bid Pack — full bundle</p>
          </div>
          <h3 className="font-heading text-lg tracking-tight font-bold text-black">Reviewer-ready bid file</h3>
          <p className="text-sm text-gray-600 mt-1">
            Seed pack + Risk register + Schedule (phases &amp; monthly grid) + Bid Pricing build-up. Hand to reviewers in one click.
          </p>
          <button
            onClick={handleBidPack}
            data-testid="bidpack-export-btn"
            className="mt-4 bg-black text-white hover:bg-[#002FA7] rounded-none px-4 py-2.5 font-bold text-sm inline-flex items-center gap-2"
          >
            <Briefcase size={14} /> Download Bid Pack (.zip)
          </button>
        </div>
      </div>

      {/* PUBLIC SHARE LINKS */}
      <SharePanel
        kind="seed"
        title="Shareable seed-pack URL"
        desc="Uploads the seed-pack ZIP to backend storage and returns a public download URL. Anyone with the link can grab the bundle."
        state={shareState.seed}
        onCreate={() => handleCreateShareLink("seed")}
        onCopy={() => handleCopy("seed")}
        onRevoke={() => handleRevoke("seed")}
        testid="seedpack-share"
        accentClass="bg-[#FF6B00] hover:bg-black"
      />

      <SharePanel
        kind="bid"
        title="Shareable bid-pack URL"
        desc="Uploads the full bid pack ZIP (seed pack + risk + schedule + pricing) to backend storage and returns a public download URL — perfect for handing reviewers the complete bid file."
        state={shareState.bid}
        onCreate={() => handleCreateShareLink("bid")}
        onCopy={() => handleCopy("bid")}
        onRevoke={() => handleRevoke("bid")}
        testid="bidpack-share"
        accentClass="bg-black hover:bg-[#002FA7]"
      />
    </section>
  );
}
